import { Link } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  PencilSimple,
  CalendarCheck,
  CreditCard,
  UserCircle,
  Heart,
  ArrowRight,
  Clock,
  Shield,
  Users,
  Handshake,
  ChatCircle,
  Sparkle
} from '@phosphor-icons/react';

const HowItWorks = () => {
  const detailedSteps = [
    {
      number: "01",
      icon: PencilSimple,
      title: "Complete Your Intake Application",
      description: "Begin by filling out our confidential online application. This helps us understand who you are, what you desire and what you're looking for in a relationship.",
      details: [
        "Be honest and open",
        "Clearly explain your goals and values",
        "Share what you're looking for in a partner",
        "Be confident and secure"
      ],
      note: "A first step to help determine goals, values and best-fit package."
    },
    {
      number: "02",
      icon: CalendarCheck,
      title: "Schedule Your Virtual Consultation",
      description: "After your application is reviewed, you'll be invited to book a one-on-one virtual consultation. This conversation allows us to get to know you personally.",
      details: [
        "Free 20-30 min virtual meeting",
        "Discuss your relationship goals",
        "Answer any questions about the process",
        "Determine if you're a good fit"
      ],
      note: "You provide at least 2 clear, up-to-date pictures of yourself (face pic and full body pic)."
    },
    {
      number: "03",
      icon: CreditCard,
      title: "Link, Laugh & Love Package",
      description: "A personalized virtual matchmaking experience designed to assist you in building a meaningful and compatible relationship.",
      details: [
        "Application Interview: Basic Information, purpose/goals, background, references/expectations, personality, compatibility, documentation and consent form",
        "Create Your Profile: Professional database entry",
        "Start Your Virtual Matchmaking Plan: Search for compatible candidates based on your unique values, lifestyle and relationship goals",
        "Private Relationship Advice (30 min): Consultation to understand your needs, desires and gain clarity on the type of partner that aligns with your lifestyle"
      ],
      note: "Additional Services: Polished Personal Résumé ($50), Extra Virtual Consultation ($50/hr), Date Planning ($25)"
    },
    {
      number: "04",
      icon: UserCircle,
      title: "Profile Creation & Match Criteria",
      description: "We'll create your personalized matchmaking profile and finalize your match preferences, values and non-negotiables. Mind, heart, faith and lifestyle alignment.",
      details: [
        "Personalized intake planning",
        "Compatibility criteria setup",
        "Values and faith alignment",
        "Lifestyle preferences"
      ],
      note: "Your matchmaker ensures connections feel natural and comfortable."
    },
    {
      number: "05",
      icon: Heart,
      title: "Matchmaking Begins",
      description: "Your matchmaker will begin the intentional search process. Matches are carefully curated and presented virtually, with guidance and support every step of the way.",
      details: [
        "Quality over quantity approach",
        "Carefully curated matches",
        "Virtual introductions",
        "Guided conversations & date support"
      ],
      note: "We focus on personality, values and real-life compatibility."
    },
    {
      number: "06",
      icon: Handshake,
      title: "Ongoing Support & Check-Ins",
      description: "You'll receive continued communication, encouragement and feedback throughout your matchmaking experience. You're never navigating love alone.",
      details: [
        "Dedicated matchmaker check-ins",
        "Private relationship advice",
        "Ongoing support and guidance",
        "Encouragement every step"
      ],
      note: "Real Love Starts with a Real Connection."
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: "Save Time",
      description: "No more endless swiping. We do the intentional searching so you can focus on meaningful connections."
    },
    {
      icon: Shield,
      title: "Safe & Respectful",
      description: "A no-pressure environment where connections happen naturally in safe, fun spaces."
    },
    {
      icon: Users,
      title: "Human Touch",
      description: "Real matchmakers who take time to know you. Not algorithms making decisions."
    }
  ];

  return (
    <PageLayout>
      {/* Hero */}
      <section className="py-24 md:py-32 rose-gradient">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full text-primary text-sm font-medium shadow-sm mb-6">
              <Sparkle weight="fill" className="w-4 h-4" />
              Virtual Matchmaking Plan
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight text-gray-800">
              Steps to <span className="text-primary">Get Started</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Your journey to finding meaningful connection begins here. We believe in 
              quality over quantity—real compatibility, real connections, not endless swiping.
            </p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-20">
            {detailedSteps.map((step, index) => (
              <div 
                key={index}
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="script-font text-6xl text-primary/30">{step.number}</span>
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <step.icon weight="light" className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                  <h2 className="font-serif text-3xl mb-4 text-gray-800">{step.title}</h2>
                  <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                    {step.description}
                  </p>
                  <ul className="space-y-3 mb-4">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-3 text-muted-foreground">
                        <Heart weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  {step.note && (
                    <p className="text-sm italic text-primary/80 mt-4 pl-4 border-l-2 border-primary/30">
                      {step.note}
                    </p>
                  )}
                </div>
                <div className={`paper-card rounded-lg aspect-square max-w-[280px] flex items-center justify-center ${
                  index % 2 === 1 ? 'md:order-1' : ''
                }`}>
                  <step.icon weight="thin" className="w-20 h-20 text-primary/30" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 rose-gradient">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="paper-card rounded-lg p-8">
            <h3 className="font-serif text-2xl mb-4 text-gray-800">Important Notes</h3>
            <ul className="space-y-3 text-muted-foreground text-left max-w-xl mx-auto">
              <li className="flex items-start gap-3">
                <Heart weight="fill" className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                All services are confidential and handled with care
              </li>
              <li className="flex items-start gap-3">
                <Heart weight="fill" className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                Fees must be paid in full before services begin
              </li>
              <li className="flex items-start gap-3">
                <Heart weight="fill" className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                Link, Laugh & Love does not guarantee relationships, only intentional connections
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-primary uppercase tracking-widest text-xs font-semibold">
              Why Choose Us
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 text-gray-800">
              The Link, Laugh & Love Difference
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="paper-card rounded-lg p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
                  <benefit.icon weight="light" className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-serif text-xl mb-3 text-gray-800">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 navy-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6 text-white">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-pink-100/80 text-lg mb-10 max-w-2xl mx-auto">
            Complete your application today and take the first step toward finding your perfect match. 
            Real Love Starts with a Real Connection.
          </p>
          <Link to="/signup">
            <Button 
              className="btn-primary text-lg px-12 py-6"
              data-testid="how-it-works-cta-btn"
            >
              <Heart weight="fill" className="w-5 h-5 mr-2" />
              Start Your Application
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </PageLayout>
  );
};

export default HowItWorks;
